import { useEffect, useState } from 'react';

/** The visual viewport must lose at least this much before the bottom bar hides (heuristic only). */
const KEYBOARD_THRESHOLD_PX = 80;
/** How long after the last visual-viewport scroll event the height transition stays disabled. */
const SCROLL_SETTLE_MS = 120;

function isTextField(el: Element | null): boolean {
  if (!el) return false;
  const tag = el.tagName;
  return (
    tag === 'TEXTAREA' ||
    (tag === 'INPUT' &&
      !/^(button|submit|checkbox|radio|file|range|hidden)$/iu.test(
        (el as HTMLInputElement).type,
      )) ||
    (el as HTMLElement).isContentEditable
  );
}

/**
 * Unconditional: the two variables always mirror the visual viewport. No "keyboard open"
 * gating for the offset, no anticipation, no throttling. `data-keyboard` is a separate
 * heuristic for hiding the bottom bar and never affects sizing.
 */
/** The visual-viewport sizing is for touch devices (the on-screen keyboard); pointers get 100dvh. */
export function isCoarsePointer(): boolean {
  try {
    return window.matchMedia('(pointer: coarse)').matches || navigator.maxTouchPoints > 0;
  } catch {
    return false;
  }
}

export function applyViewport(): void {
  const vv = window.visualViewport;
  const root = document.documentElement;
  if (!vv || !isCoarsePointer()) return;
  root.style.setProperty('--vv-height', `${vv.height}px`);
  root.style.setProperty('--vv-top', `${vv.offsetTop}px`);
  root.toggleAttribute('data-keyboard', window.innerHeight - vv.height > KEYBOARD_THRESHOLD_PX);
}

/**
 * Sizes the fixed app root from the VisualViewport API: on every visualViewport resize and
 * scroll event (and on window scroll) `--vv-height` = visualViewport.height and `--vv-top` =
 * visualViewport.offsetTop, written synchronously in the handler. The root is
 * `position: fixed; top: var(--vv-top); height: var(--vv-height); width: 100%` (index.css).
 *
 * The height transition (iOS keyboard curve) is enabled only while a text field is focused
 * (`data-typing`) and disabled during visual-viewport scroll events (`data-vv-scrolling`), so
 * the header never lags behind the viewport.
 *
 * Android Chrome with `interactive-widget=resizes-content` resizes the layout viewport, so
 * visualViewport.height equals innerHeight and offsetTop stays 0: same code, no double-apply.
 */
export function useVisualViewport(): void {
  useEffect(() => {
    const vv = window.visualViewport;
    const root = document.documentElement;
    // M5.2: only coarse-pointer (touch) devices size the root from the visual viewport.
    if (!vv || !isCoarsePointer()) return;
    let settle: ReturnType<typeof setTimeout> | undefined;
    const onResize = () => applyViewport();
    const onScroll = () => {
      root.setAttribute('data-vv-scrolling', '');
      applyViewport();
      clearTimeout(settle);
      settle = setTimeout(() => root.removeAttribute('data-vv-scrolling'), SCROLL_SETTLE_MS);
    };
    const onFocusIn = (e: FocusEvent) => {
      if (isTextField(e.target as Element | null)) root.setAttribute('data-typing', '');
    };
    const onFocusOut = () => {
      // The next focus target is not known yet; check once focus has settled.
      setTimeout(() => {
        if (!isTextField(document.activeElement)) root.removeAttribute('data-typing');
      }, 0);
    };
    applyViewport();
    vv.addEventListener('resize', onResize);
    vv.addEventListener('scroll', onScroll);
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('orientationchange', onResize);
    document.addEventListener('focusin', onFocusIn);
    document.addEventListener('focusout', onFocusOut);
    return () => {
      clearTimeout(settle);
      vv.removeEventListener('resize', onResize);
      vv.removeEventListener('scroll', onScroll);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('orientationchange', onResize);
      document.removeEventListener('focusin', onFocusIn);
      document.removeEventListener('focusout', onFocusOut);
      root.style.removeProperty('--vv-height');
      root.style.removeProperty('--vv-top');
      root.removeAttribute('data-keyboard');
      root.removeAttribute('data-typing');
      root.removeAttribute('data-vv-scrolling');
    };
  }, []);
}

/** On composer blur and on every route change: document back at the top, then re-measured. */
export function resetViewport(): void {
  window.scrollTo(0, 0);
  applyViewport();
}

/** Marks the root while the composer has focus; CSS hides the bottom bar. */
export function setComposerFocus(focused: boolean): void {
  document.documentElement.toggleAttribute('data-composer-focus', focused);
  if (!focused) setTimeout(resetViewport, 0);
}

/** True after `delayMs` of `active` being continuously true; false as soon as it turns false. */
export function useDelayedFlag(active: boolean, delayMs: number): boolean {
  const [on, setOn] = useState(false);
  const [wasActive, setWasActive] = useState(active);
  // Reset synchronously during render when the condition clears (no effect-driven setState).
  if (wasActive !== active) {
    setWasActive(active);
    if (!active) setOn(false);
  }
  useEffect(() => {
    if (!active) return;
    const t = setTimeout(() => setOn(true), delayMs);
    return () => clearTimeout(t);
  }, [active, delayMs]);
  return on && active;
}

/** Debug overlay switch: `?vv=1` in the URL (remembered) or localStorage `pa.vvdebug=1`. */
export function viewportDebugEnabled(): boolean {
  try {
    if (new URLSearchParams(window.location.search).get('vv') === '1') {
      localStorage.setItem('pa.vvdebug', '1');
      return true;
    }
    return localStorage.getItem('pa.vvdebug') === '1';
  } catch {
    return false;
  }
}

export function isIos(): boolean {
  const ua = navigator.userAgent;
  return (
    /iPhone|iPad|iPod/.test(ua) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  );
}

export function isStandalone(): boolean {
  return (
    (navigator as Navigator & { standalone?: boolean }).standalone === true ||
    window.matchMedia('(display-mode: standalone)').matches
  );
}

/** Major iOS version from the user agent, or undefined off iOS. */
export function iosMajorVersion(): number | undefined {
  const m = /OS (\d+)_/.exec(navigator.userAgent);
  return m ? Number(m[1]) : undefined;
}

/** iOS Safari (not already installed to the Home Screen). */
export function isIosSafariBrowser(): boolean {
  const ua = navigator.userAgent;
  const safari = /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS|OPiOS/.test(ua);
  return isIos() && safari && !isStandalone();
}
